import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  loginWithEmail, 
  loginWithGoogle, 
  registerUser, 
  clearError,
  sendVerificationEmail,
  setShowEmailVerificationModal,
  setIsResendingEmail 
} from './authSlice';
import PasswordResetModal from './PasswordResetModal';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import EmailVerificationModal from './EmailVerificationModal';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Modal,
  IconButton,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CloseIcon from '@mui/icons-material/Close';

const AuthModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { status, error, showEmailVerificationModal, isResendingEmail } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0); // 0: ログイン, 1: 新規登録
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [validatedFormData, setValidatedFormData] = useState(null);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'パスワードは8文字以上である必要があります';
    }
    if (!/\d/.test(password)) {
      return 'パスワードには数字を含める必要があります';
    }
    return '';
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPasswordError('');
    if (error) dispatch(clearError());
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) dispatch(clearError());
    if (name === 'password') {
      setPasswordError('');
    }
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) dispatch(clearError());
    if (name === 'confirmPassword' || name === 'password') {
      setPasswordError('');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const passwordValidationError = validatePassword(loginData.password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }
    dispatch(loginWithEmail(loginData));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const passwordValidationError = validatePassword(registerData.password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      setPasswordError('パスワードが一致しません');
      return;
    }
    setValidatedFormData({
      email: registerData.email,
      password: registerData.password
    });
    setShowPrivacyPolicy(true);
  };

  const handleGoogleAuth = () => {
    dispatch(loginWithGoogle());
  };

  const handleClose = () => {
    setActiveTab(0);
    setLoginData({ email: '', password: '' });
    setRegisterData({ email: '', password: '', confirmPassword: '' });
    setPasswordError('');
    if (error) dispatch(clearError());
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            width: '90%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: '#1a1a1f',
            border: '2px solid #00bcd4',
            boxShadow: '0 0 40px rgba(0, 188, 212, 0.3)',
            borderRadius: 2,
            p: 4,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#ffffff',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography 
            variant="h4" 
            component="h1" 
            align="center" 
            sx={{ 
              fontWeight: 700,
              color: '#00bcd4',
              mb: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            {activeTab === 0 ? 'ログイン' : '新規登録'}
          </Typography>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': {
                bgcolor: '#00bcd4',
                height: 3,
              },
              '& .MuiTab-root': {
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '1rem',
                '&.Mui-selected': {
                  color: '#00bcd4',
                },
              },
            }}
          >
            <Tab label="ログイン" />
            <Tab label="新規登録" />
          </Tabs>

          {(error || passwordError) && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                bgcolor: 'rgba(244, 67, 54, 0.1)',
                color: '#f44336',
                '& .MuiAlert-icon': {
                  color: '#f44336',
                }
              }}
            >
              {error || passwordError}
            </Alert>
          )}

          {activeTab === 0 ? (
            // ログインフォーム
            <form onSubmit={handleLoginSubmit}>
              <TextField
                fullWidth
                label="メールアドレス"
                name="email"
                type="email"
                value={loginData.email}
                onChange={handleLoginChange}
                margin="normal"
                required
                autoComplete="email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00bcd4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#00bcd4',
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="パスワード"
                name="password"
                type="password"
                value={loginData.password}
                onChange={handleLoginChange}
                margin="normal"
                required
                autoComplete="current-password"
                helperText="8文字以上で、数字を1文字以上含める必要があります"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00bcd4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#00bcd4',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              />
              
              <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPasswordReset(true);
                  }}
                  sx={{
                    color: '#00bcd4',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  パスワードを忘れた場合はこちら
                </Link>
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 2, 
                  mb: 2, 
                  py: 1.5,
                  bgcolor: '#00bcd4',
                  color: '#000000',
                  fontWeight: 700,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#00acc1',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(0, 188, 212, 0.3)',
                  }
                }}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <CircularProgress size={24} sx={{ color: '#000000' }} />
                ) : (
                  'ログイン'
                )}
              </Button>
            </form>
          ) : (
            // 新規登録フォーム
            <form onSubmit={handleRegisterSubmit}>
              <TextField
                fullWidth
                label="メールアドレス"
                name="email"
                type="email"
                value={registerData.email}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00bcd4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#00bcd4',
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="パスワード"
                name="password"
                type="password"
                value={registerData.password}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="new-password"
                helperText="8文字以上で、数字を1文字以上含める必要があります"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00bcd4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#00bcd4',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              />
              <TextField
                fullWidth
                label="パスワード（確認）"
                name="confirmPassword"
                type="password"
                value={registerData.confirmPassword}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="new-password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00bcd4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#00bcd4',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  mb: 2, 
                  py: 1.5,
                  bgcolor: '#00bcd4',
                  color: '#000000',
                  fontWeight: 700,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#00acc1',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(0, 188, 212, 0.3)',
                  }
                }}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <CircularProgress size={24} sx={{ color: '#000000' }} />
                ) : (
                  '新規登録'
                )}
              </Button>
            </form>
          )}

          <Divider sx={{ 
            my: 3,
            '&::before, &::after': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiDivider-wrapper': {
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600,
            }
          }}>
            または
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleAuth}
            disabled={status === 'loading'}
            sx={{ 
              py: 1.5,
              borderColor: '#ffffff',
              color: '#ffffff',
              fontWeight: 700,
              '&:hover': {
                borderColor: '#00bcd4',
                color: '#00bcd4',
                bgcolor: 'rgba(0, 188, 212, 0.1)',
              },
            }}
          >
            Googleで{activeTab === 0 ? 'ログイン' : '登録'}
          </Button>

          {activeTab === 0 && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                アカウントをお持ちでない方は
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(1);
                  }}
                  sx={{
                    color: '#00bcd4',
                    textDecoration: 'none',
                    fontWeight: 600,
                    ml: 0.5,
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  新規登録
                </Link>
              </Typography>
            </Box>
          )}
        </Paper>
      </Modal>

      <PasswordResetModal 
        open={showPasswordReset} 
        onClose={() => setShowPasswordReset(false)}
      />

      <PrivacyPolicyModal
        open={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
        onAgree={() => {
          setShowPrivacyPolicy(false);
          if (validatedFormData) {
            dispatch(registerUser(validatedFormData));
          }
        }}
      />

      <EmailVerificationModal
        open={showEmailVerificationModal}
        onClose={() => dispatch(setShowEmailVerificationModal(false))}
        email={registerData.email}
        onResendEmail={async () => {
          dispatch(setIsResendingEmail(true));
          await dispatch(sendVerificationEmail());
          dispatch(setIsResendingEmail(false));
        }}
        isResending={isResendingEmail}
      />
    </>
  );
};

export default AuthModal;
